/**
 * Workspace working / books date — the admin-controlled calendar for the whole app.
 *
 * Finance shops post with backdated or forward-dated entries. Due status, overdue
 * days, EMI charging, and interest accrual must follow this date (or the latest
 * admin entry), never the device's wall-clock "today".
 */
import { loadDevJson, saveDevJson } from './devStorage';

export const WORKSPACE_AS_OF_KEY = 'ef_workspace_as_of_v1';
export const WORKSPACE_AS_OF_EVENT = 'ef-workspace-as-of';

function emit() {
  try {
    window.dispatchEvent(new Event(WORKSPACE_AS_OF_EVENT));
  } catch {
    /* ignore */
  }
}

function toDateOnly(isoOrYmd: string): string {
  const s = String(isoOrYmd || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Noon local-ish ISO for stable comparisons (avoids DST edge issues in UI). */
export function workspaceDateToMs(ymd: string): number {
  const d = toDateOnly(ymd);
  if (!d) return 0;
  const t = new Date(`${d}T12:00:00`).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Today's calendar date as YYYY-MM-DD (local). */
export function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Validate a working date (YYYY-MM-DD). Blank → wall-clock today.
 * Past and future dates are allowed (future supports navbar testing).
 */
export function normalizeWorkingDate(ymd: string | null | undefined): string {
  const d = ymd ? toDateOnly(ymd) : '';
  if (!d) return todayYmd();
  return d;
}

/** @deprecated alias — no longer clamps future dates */
export const clampWorkingDate = normalizeWorkingDate;

/** Read the admin working date (YYYY-MM-DD), or '' if unset. */
export function readWorkspaceAsOfDate(): string {
  try {
    const raw = localStorage.getItem(WORKSPACE_AS_OF_KEY);
    if (!raw) return '';
    return toDateOnly(raw);
  } catch {
    return '';
  }
}

/** Persist admin working date. Pass null/'' to clear (fall back to derived / today). */
export function setWorkspaceAsOfDate(ymd: string | null): void {
  try {
    if (!ymd) {
      localStorage.removeItem(WORKSPACE_AS_OF_KEY);
    } else {
      localStorage.setItem(WORKSPACE_AS_OF_KEY, normalizeWorkingDate(ymd));
    }
  } catch {
    /* ignore */
  }
  emit();
}

/**
 * Derive books date from the newest admin-entered operational timestamp.
 * Used when no explicit working date is set.
 */
export function deriveWorkspaceAsOfFromEntries(timestamps: Array<string | null | undefined>): string {
  let max = 0;
  for (const raw of timestamps) {
    if (!raw) continue;
    const t = new Date(raw).getTime();
    if (Number.isFinite(t) && t > max) max = t;
  }
  if (!max) return '';
  const d = new Date(max);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Active books date for calculations (YYYY-MM-DD).
 * Priority: admin working date → derived from entries → wall-clock today.
 */
export function getWorkspaceAsOfDate(derivedCandidates?: Array<string | null | undefined>): string {
  const manual = readWorkspaceAsOfDate();
  if (manual) return normalizeWorkingDate(manual);
  if (derivedCandidates?.length) {
    const derived = deriveWorkspaceAsOfFromEntries(derivedCandidates);
    if (derived) return toDateOnly(derived) || todayYmd();
  }
  return todayYmd();
}

/** True when navbar working date differs from the device calendar today. */
export function isWorkspaceDateOverridden(): boolean {
  const manual = readWorkspaceAsOfDate();
  if (!manual) return false;
  return normalizeWorkingDate(manual) !== todayYmd();
}

/**
 * Milliseconds for ledger / due / overdue math.
 * Never falls back to Date.now() unless `allowWallClockFallback` is true
 * (only for empty brand-new workspaces with no entries and no working date).
 */
export function getWorkspaceAsOfMs(
  derivedCandidates?: Array<string | null | undefined>,
  opts?: { allowWallClockFallback?: boolean }
): number {
  const ymd = getWorkspaceAsOfDate(derivedCandidates);
  if (ymd) return workspaceDateToMs(ymd);
  if (opts?.allowWallClockFallback) return Date.now();
  return 0;
}

/** Default value for date inputs (working date, else empty so admin must pick). */
export function defaultAdminEntryDate(derivedCandidates?: Array<string | null | undefined>): string {
  return getWorkspaceAsOfDate(derivedCandidates);
}

/** Compare YYYY-MM-DD strings (lexicographic works for ISO dates). */
export function isOnOrBeforeBooksDate(dateYmd: string, asOfYmd: string): boolean {
  if (!dateYmd || !asOfYmd) return false;
  return dateYmd <= asOfYmd;
}

export function useWorkspaceAsOf(): {
  asOfDate: string;
  setAsOfDate: (ymd: string | null) => void;
  asOfMs: number;
} {
  // Lightweight hook without importing React at module top for non-React callers.
  // Prefer importing React useSyncExternalStore in UI files; this helper is for store code.
  const asOfDate = readWorkspaceAsOfDate();
  return {
    asOfDate,
    setAsOfDate: setWorkspaceAsOfDate,
    asOfMs: asOfDate ? workspaceDateToMs(asOfDate) : 0,
  };
}

/** Collect common entry timestamps from local caches for derive fallback. */
export function collectWorkspaceEntryTimestamps(): string[] {
  const out: string[] = [];
  try {
    const payments = loadDevJson<Array<{ paidAt?: string }>>('admin_emi_payments_v1', []);
    for (const p of payments) if (p.paidAt) out.push(p.paidAt);
  } catch {
    /* ignore */
  }
  try {
    const fins = loadDevJson<Array<{ createdAt?: string; data?: { agreementDate?: string; startDate?: string } }>>(
      'finance_records_v1',
      []
    );
    for (const r of fins) {
      if (r.createdAt) out.push(r.createdAt);
      const d = r.data?.agreementDate || r.data?.startDate;
      if (d) out.push(String(d));
    }
  } catch {
    /* ignore */
  }
  try {
    const hands = loadDevJson<Array<{ createdAt?: string; data?: { agreementDate?: string; startDate?: string } }>>(
      'hand_loan_records_v1',
      []
    );
    for (const r of hands) {
      if (r.createdAt) out.push(r.createdAt);
      const d = r.data?.agreementDate || r.data?.startDate;
      if (d) out.push(String(d));
    }
  } catch {
    /* ignore */
  }
  try {
    const bundle = loadDevJson<{
      expenses?: Array<{ expenseDate?: string }>;
      advances?: Array<{ advanceDate?: string }>;
      salaryPayments?: Array<{ paymentDate?: string }>;
    }>('accounting_bundle_v1', {});
    for (const e of bundle.expenses || []) if (e.expenseDate) out.push(e.expenseDate);
    for (const a of bundle.advances || []) if (a.advanceDate) out.push(a.advanceDate);
    for (const s of bundle.salaryPayments || []) if (s.paymentDate) out.push(s.paymentDate);
  } catch {
    /* ignore */
  }
  return out;
}

/** Resolve books ms using working date or all known workspace entries. */
export function resolveWorkspaceAsOfMs(opts?: { allowWallClockFallback?: boolean }): number {
  return getWorkspaceAsOfMs(collectWorkspaceEntryTimestamps(), opts);
}

/** Resolve books YYYY-MM-DD. */
export function resolveWorkspaceAsOfDate(): string {
  return getWorkspaceAsOfDate(collectWorkspaceEntryTimestamps());
}

/**
 * ISO timestamp for an admin-entered calendar date (noon local).
 * Uses working / books date when `ymd` is blank. Last resort: wall clock
 * only when the workspace has no working date and no entries yet.
 */
export function adminEntryIso(ymd?: string | null): string {
  const d =
    toDateOnly(String(ymd || '')) ||
    defaultAdminEntryDate() ||
    resolveWorkspaceAsOfDate();
  if (d) {
    const t = new Date(`${d}T12:00:00`).toISOString();
    if (Number.isFinite(Date.parse(t))) return t;
  }
  const fallback = resolveWorkspaceAsOfDate();
  if (fallback) {
    const t = new Date(`${fallback}T12:00:00`).toISOString();
    if (Number.isFinite(Date.parse(t))) return t;
  }
  return new Date().toISOString();
}

/** YYYY-MM-DD for forms — working date, else empty. */
export function adminEntryDateOnly(ymd?: string | null): string {
  return toDateOnly(String(ymd || '')) || defaultAdminEntryDate() || resolveWorkspaceAsOfDate();
}

/** Keep saveDevJson import used if tree-shaken — also used when writing from settings. */
export function persistWorkspaceAsOfMirror(ymd: string): void {
  saveDevJson(WORKSPACE_AS_OF_KEY, { date: toDateOnly(ymd) });
}
